/**
 * TransferSection - Displays transfers between accounts
 *
 * WP-TRANSFER-2: This section appears below the main Sankey flow diagram and shows
 * ALL money movements between accounts. Transfers are EXCLUDED from the main diagram.
 *
 * Transfer types displayed:
 * - Paired: Both source and target accounts are tracked
 * - Single-sided outgoing: Source tracked, target is "[External]"
 * - Single-sided incoming: Source is "[External]", target tracked
 *
 * Each transfer shows: Source → Target with amount and subcategory label.
 *
 * @deprecated Use TransferSankey instead (WP-FLOW-REFACTOR-2).
 * This component is kept for backward compatibility during migration.
 */

import { memo, useState, useCallback } from 'react';
import type { TransferData } from './types';
import { TransferEdge } from './edges';
import { FlowTooltip, type TooltipContent } from './FlowTooltip';
import { COLORS } from './constants';
import { cn } from '@/utils/cn';

export interface TransferSectionProps {
  transfers: TransferData[];
  /** Called when a transfer is clicked. IDs may be null for external accounts. */
  onTransferClick?: (sourceId: string | null, targetId: string | null) => void;
  onTransferHover?: (transfer: TransferData | null, event?: React.MouseEvent) => void;
  className?: string;
}

/**
 * Check if an account name represents an external/untracked account
 */
function isExternalAccount(name: string): boolean {
  return name === '[External]' || name.toLowerCase().includes('external');
}

/**
 * Format currency amount for display
 */
function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Layout constants for the transfer section
const SECTION_PADDING = 24;
const ITEM_HEIGHT = 60;
const ITEM_GAP = 12;
const ACCOUNT_BOX_WIDTH = 120;
const ACCOUNT_BOX_HEIGHT = 36;
const MIN_LINE_LENGTH = 100;

function TransferSectionComponent({
  transfers,
  onTransferClick,
  onTransferHover,
  className,
}: TransferSectionProps) {
  // Tooltip state
  const [tooltip, setTooltip] = useState<TooltipContent | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [highlightedTransferId, setHighlightedTransferId] = useState<string | null>(null);

  // Handle transfer hover
  const handleTransferHover = useCallback(
    (transfer: TransferData | null, hovering: boolean, event?: React.MouseEvent) => {
      if (!hovering || !transfer) {
        setTooltip(null);
        setHighlightedTransferId(null);
        onTransferHover?.(null);
        return;
      }

      setHighlightedTransferId(transfer.id);
      if (event) {
        setTooltipPosition({ x: event.clientX, y: event.clientY });
      }

      setTooltip({
        type: 'edge',
        source: transfer.sourceAccountName,
        target: transfer.targetAccountName,
        value: transfer.amount,
      });

      onTransferHover?.(transfer, event);
    },
    [onTransferHover]
  );

  // Handle transfer click
  // WP-TRANSFER-2: Pass null IDs for external accounts
  const handleTransferClick = useCallback(
    (transfer: TransferData) => {
      // Only trigger click if at least one account is tracked (not external)
      if (transfer.sourceAccountId || transfer.targetAccountId) {
        onTransferClick?.(transfer.sourceAccountId, transfer.targetAccountId);
      }
    },
    [onTransferClick]
  );

  // Don't render if no transfers
  if (!transfers || transfers.length === 0) {
    return null;
  }

  // Calculate section dimensions
  const sectionHeight = SECTION_PADDING * 2 + transfers.length * ITEM_HEIGHT + (transfers.length - 1) * ITEM_GAP;
  const sectionWidth = ACCOUNT_BOX_WIDTH * 2 + MIN_LINE_LENGTH + SECTION_PADDING * 3;

  return (
    <div className={cn('transfer-section', className)}>
      {/* Section header */}
      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
        <span
          className="w-3 h-3 rounded-full"
          style={{ backgroundColor: COLORS.transfer.border }}
          aria-hidden="true"
        />
        Transfers Between Accounts
      </h3>

      {/* Transfer diagram */}
      <div
        className="bg-gray-50 rounded-lg border border-gray-200 overflow-x-auto"
        style={{ minHeight: sectionHeight }}
      >
        <svg
          width={sectionWidth}
          height={sectionHeight}
          viewBox={`0 0 ${sectionWidth} ${sectionHeight}`}
          role="img"
          aria-label={`${transfers.length} transfer${transfers.length !== 1 ? 's' : ''} between accounts`}
          className="transfer-section-svg"
        >
          {transfers.map((transfer, index) => {
            const y = SECTION_PADDING + index * (ITEM_HEIGHT + ITEM_GAP) + ITEM_HEIGHT / 2;
            const isHighlighted = highlightedTransferId === transfer.id;
            const isSourceExternal = isExternalAccount(transfer.sourceAccountName);
            const isTargetExternal = isExternalAccount(transfer.targetAccountName);

            return (
              <g key={transfer.id} className="transfer-item">
                {/* Source account box */}
                <g className="source-account">
                  <rect
                    x={SECTION_PADDING}
                    y={y - ACCOUNT_BOX_HEIGHT / 2}
                    width={ACCOUNT_BOX_WIDTH}
                    height={ACCOUNT_BOX_HEIGHT}
                    rx={4}
                    ry={4}
                    fill={isSourceExternal ? '#F3F4F6' : 'white'}
                    stroke={isHighlighted ? COLORS.transfer.border : (isSourceExternal ? '#9CA3AF' : COLORS.accountTierA)}
                    strokeWidth={isHighlighted ? 2 : 1}
                    strokeDasharray={isSourceExternal ? '4 2' : 'none'}
                    className="transition-all duration-150"
                  />
                  <text
                    x={SECTION_PADDING + ACCOUNT_BOX_WIDTH / 2}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={isSourceExternal ? '#6B7280' : COLORS.text.primary}
                    fontSize={12}
                    fontWeight={isSourceExternal ? 400 : 500}
                    fontStyle={isSourceExternal ? 'italic' : 'normal'}
                    className="select-none"
                  >
                    {transfer.sourceAccountName.length > 12
                      ? `${transfer.sourceAccountName.slice(0, 12)}...`
                      : transfer.sourceAccountName}
                  </text>
                </g>

                {/* Transfer edge (dashed line with arrow) */}
                <TransferEdge
                  sourceAccount={transfer.sourceAccountName}
                  targetAccount={transfer.targetAccountName}
                  amount={transfer.amount}
                  sourceX={SECTION_PADDING + ACCOUNT_BOX_WIDTH + 8}
                  sourceY={y}
                  targetX={sectionWidth - SECTION_PADDING - ACCOUNT_BOX_WIDTH - 8}
                  targetY={y}
                  onClick={() => handleTransferClick(transfer)}
                  onHover={(hovering, event) => handleTransferHover(transfer, hovering, event)}
                  isHighlighted={isHighlighted}
                />

                {/* Subcategory label (WP-TRANSFER-2) */}
                {transfer.subcategory && transfer.subcategory !== 'Transfer' && (
                  <text
                    x={sectionWidth / 2}
                    y={y + 20}
                    textAnchor="middle"
                    fill="#6B7280"
                    fontSize={10}
                    className="select-none"
                  >
                    {transfer.subcategory}
                  </text>
                )}

                {/* Target account box */}
                <g className="target-account">
                  <rect
                    x={sectionWidth - SECTION_PADDING - ACCOUNT_BOX_WIDTH}
                    y={y - ACCOUNT_BOX_HEIGHT / 2}
                    width={ACCOUNT_BOX_WIDTH}
                    height={ACCOUNT_BOX_HEIGHT}
                    rx={4}
                    ry={4}
                    fill={isTargetExternal ? '#F3F4F6' : 'white'}
                    stroke={isHighlighted ? COLORS.transfer.border : (isTargetExternal ? '#9CA3AF' : COLORS.accountTierA)}
                    strokeWidth={isHighlighted ? 2 : 1}
                    strokeDasharray={isTargetExternal ? '4 2' : 'none'}
                    className="transition-all duration-150"
                  />
                  <text
                    x={sectionWidth - SECTION_PADDING - ACCOUNT_BOX_WIDTH / 2}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={isTargetExternal ? '#6B7280' : COLORS.text.primary}
                    fontSize={12}
                    fontWeight={isTargetExternal ? 400 : 500}
                    fontStyle={isTargetExternal ? 'italic' : 'normal'}
                    className="select-none"
                  >
                    {transfer.targetAccountName.length > 12
                      ? `${transfer.targetAccountName.slice(0, 12)}...`
                      : transfer.targetAccountName}
                  </text>
                </g>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Total transfers summary */}
      <div className="mt-2 text-xs text-gray-500">
        {transfers.length} transfer{transfers.length !== 1 ? 's' : ''} totaling{' '}
        {formatAmount(transfers.reduce((sum, t) => sum + t.amount, 0))}
      </div>

      {/* Tooltip */}
      <FlowTooltip content={tooltip} position={tooltipPosition} />
    </div>
  );
}

export const TransferSection = memo(TransferSectionComponent);
export default TransferSection;
